// @unocss-include — this file holds class strings (cva variants). UnoCSS only
// extracts from files its pipeline filter accepts (default: vue/jsx/tsx/html/…,
// NOT .ts), and that filter is applied to `content.filesystem` matches too, so
// without this marker a consumer app never generated these classes; measured
// 2026-10-02 in elveris-webrtc and elveris-avm: `will-change-transform`,
// `[&_svg]:size-4`, `active:scale-95`, `hover:bg-theme-brand/90` had no CSS.
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'

export { default as Button } from './Button.vue'
export { default as ButtonLoading } from './ButtonLoading.vue'

export const buttonVariants = cva(
  // Base styles with UnoCSS logical properties and enhanced accessibility
  // `bg-transparent` resets the browser-native button background (#efefef
  // bleeds through under @unocss/reset/tailwind-compat). Variants that
  // need a fill still override via `bg-theme-*`.
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium bg-transparent ' +
    // Transitions with motion-safe support
  'transition-theme motion-reduce:transition-none ' +
    // Focus ring: 2px in the 3:1-corrected ring colour, separated from the
    // button's own fill by a 2px gap in the page colour. It was `ring-theme-brand`,
    // a kit rule that writes the WHOLE `box-shadow` (`0 0 0 3px <ring>`) and so
    // replaced the composed ring of `ring-2` + `ring-offset-2`: the offset never
    // rendered and the ring sat directly on the fill (1.00:1 against a brand fill).
    // `ring-primary` is the kit's STATIC colour-only rule (`--un-ring-color` =
    // `--theme-ring-readable`); `ring-ring` would resolve through the app's own
    // `theme.colors.ring`, which consumer configs have copied as raw brand.
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-theme-bg ' +
    // Disabled states with better UX
  'disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed ' +
    // Enhanced SVG handling
  '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 ' +
    // Touch optimization
  'touch-manipulation select-none ' +
    // Performance optimization
  'will-change-transform',
  {
    variants: {
      variant: {
        // Enhanced default with theme-aware colors
        default: 'bg-theme-brand text-theme-brand-on shadow-modern-sm hover:bg-theme-brand/90 hover:shadow-modern-md',
        // Destructive with better visual feedback
        destructive: 'bg-theme-err text-theme-err-on shadow-modern-sm hover:bg-theme-err/90 hover:shadow-modern-md',
        // Outline with theme-aware border and hover
        outline: 'border border-theme-neutral-3 bg-theme-surface-1 text-theme-fg hover:bg-theme-surface-2 hover:border-theme-neutral-4',
        // Secondary with enhanced contrast
        secondary: 'bg-theme-neutral-2 text-theme-fg shadow-modern-sm hover:bg-theme-neutral-3 hover:shadow-modern-md',
        // Semantic status surfaces for selected states and compact toolbars.
        success: 'bg-theme-ok/10 text-theme-ok border border-theme-ok/20 hover:bg-theme-ok/20 hover:border-theme-ok/30',
        warning: 'bg-theme-warn/10 text-theme-warn border border-theme-warn/20 hover:bg-theme-warn/20 hover:border-theme-warn/30',
        info: 'bg-theme-info/10 text-theme-info border border-theme-info/20 hover:bg-theme-info/20 hover:border-theme-info/30',
        // Ghost with subtle interactions
        ghost: 'text-theme-fg hover:bg-theme-surface-1 active:bg-theme-surface-2',
        // Quiet toolbar actions use the muted text token until hover, when
        // they return to the normal foreground for a clear interaction cue.
        'ghost-muted': 'text-muted-foreground hover:bg-theme-surface-1 hover:text-theme-fg active:bg-theme-surface-2',
        // Link with smooth underline animation
        link: 'text-theme-brand underline-offset-4 hover:underline decoration-2 decoration-theme-brand/50 hover:decoration-theme-brand',
        // Enhanced color variants with theme-aware styles
        sky: 'bg-theme-info/10 text-theme-info border border-theme-info/20 hover:bg-theme-info/20 hover:border-theme-info/30',
        amber: 'bg-theme-warn/10 text-theme-warn border border-theme-warn/20 hover:bg-theme-warn/20 hover:border-theme-warn/30',
        violet: 'bg-theme-brand/10 text-theme-brand border border-theme-brand/20 hover:bg-theme-brand/20 hover:border-theme-brand/30',
        rose: 'bg-theme-err/10 text-theme-err border border-theme-err/20 hover:bg-theme-err/20 hover:border-theme-err/30',
        // Enhanced glassmorphism
        glass: 'backdrop-blur-md bg-theme-surface-1/80 border border-theme-neutral-3/30 text-theme-fg shadow-modern-sm hover:bg-theme-surface-2/80',
        'glass-solid': 'backdrop-blur-lg bg-theme-card/90 border border-theme-neutral-3/40 text-theme-fg shadow-modern-md',

        // Advanced gradient variants
        gradient: 'bg-gradient-to-r from-theme-brand to-theme-brand/80 text-theme-brand-on border-0 shadow-modern-sm hover:from-theme-brand/90 hover:to-theme-brand/70',
        'gradient-rainbow':
          'bg-gradient-rainbow text-rainbow-on border-0 shadow-modern-colored hover:shadow-modern-lg',

        // Neumorphism variants
        neu: 'bg-theme-surface-1 border border-theme-neutral-3/50 text-theme-fg shadow-modern-sm hover:bg-theme-surface-2',
        'neu-inset': 'bg-theme-surface-2 border border-theme-neutral-3/50 text-theme-fg shadow-inset-sm',

        // Glow variants with animation
        glow: 'bg-theme-brand text-theme-brand-on border border-theme-brand/50 shadow-modern-glow hover:shadow-modern-glow-lg',
        'glow-success': 'bg-theme-ok text-theme-ok-on border border-theme-ok/50 shadow-modern-glow hover:shadow-modern-glow-lg',
        'glow-warning': 'bg-theme-warn text-theme-warn-on border border-theme-warn/50 shadow-modern-glow hover:shadow-modern-glow-lg',
        'glow-danger': 'bg-theme-err text-theme-err-on border border-theme-err/50 shadow-modern-glow hover:shadow-modern-glow-lg',

        // Modern variants
        modern: 'bg-theme-brand text-theme-brand-on shadow-modern-md hover:bg-theme-brand/90 hover:shadow-modern-lg',
        'modern-subtle': 'bg-theme-surface-1 border border-theme-neutral-3 text-theme-fg shadow-modern-sm hover:bg-theme-surface-2 hover:shadow-modern-md',
      },
      size: {
        // Sprint S-RADIUS-PARITY-1 (2026-06-04) — Button/Input radii now share
        // the same scale so an `<Input size="md">` and `<Button size="md">`
        // line up geometrically. Earlier Button md=6px (rounded-md) while
        // Input md=12px (rounded-xl), making side-by-side controls look
        // mismatched. New scale: sm→lg (8), md→xl (12), lg→2xl (16) —
        // matches Input.vue exactly. xs raised to h-8 (32px) and
        // responsive mobile branch raised to h-10 (40px) so the smallest
        // tap targets clear WCAG 2.5.5 (≥24×24 CSS px effective target)
        // and Apple HIG (44pt on touch surfaces — phone variant uses h-14).
        default: 'ui-md rounded-xl',
        // Compact (dense tools): 20px visual, 11px text; `ui-hit-24` keeps the
        // target at 24x24 (WCAG 2.5.8) with a transparent ::before.
        '2xs': 'ui-2xs rounded-md ui-hit-24 [&_svg]:size-3',
        // Dense toolbars: 28px on desktop, 44px on narrow screens so compact
        // labels remain comfortable to tap without call-site height overrides.
        compact: 'h-11 sm:h-7 rounded-lg px-2.5 text-xs gap-1.5 font-medium transition-theme [&_svg]:size-3',
        xs: 'h-8 rounded-lg pi-2.5 text-xs gap-1 [&_svg]:size-3',
        sm: 'ui-sm rounded-lg [&_svg]:size-3.5',
        md: 'ui-md rounded-xl [&_svg]:size-4',
        lg: 'ui-lg rounded-2xl [&_svg]:size-5',
        xl: 'h-12 rounded-2xl pi-10 text-base gap-4 [&_svg]:size-6',
        icon: 'h-10 w-10 rounded-xl p-0 gap-0',
        // Mobile morse keypad — stays larger (HIG ≥44pt for one-handed touch).
        morse: 'h-12 min-w-12 rounded-2xl pi-3 pb-2 text-2xl phone:h-14 phone:min-w-14 phone:text-3xl',
        // Responsive: mobile baseline raised h-8→h-10 so 375px viewports
        // don't ship 32px buttons that fail WCAG.
        responsive: 'h-10 pi-3 text-xs sm:h-10 sm:px-4 sm:text-sm md:h-12 md:px-6 md:text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
